import AuthService from './AuthService';

class OrderService {
  static getBaseUrl() {
    // Configured absolute OJM URL wins; otherwise fall back to the app's
    // mount path so same-origin /api calls carry the sub-path prefix
    // (e.g. /ui-mock/api/orders) and route back through the same ingress.
    const configured = (window.APP_CONFIG.ojmBaseUrl || '').replace(/\/$/, '');
    return configured || (process.env.PUBLIC_URL || '');
  }

  /**
   * Fetches a paginated list of orders, then fetches details for each order.
   * @param {number} page - The page number to fetch
   * @returns {Promise<Object>} The JSON response containing the orders
   */
  static async getOrders(page = 0) {
    let endpoint = `${this.getBaseUrl()}/api/orders?page=${page}`;
    if (window.APP_CONFIG.featureFlags?.useDummyData) {
      endpoint = `${process.env.PUBLIC_URL}/dummy-data/orders.json`;
    }

    const response = await fetch(endpoint, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${AuthService.getToken()}`,
      },
    });

    if (response.status === 401) {
      AuthService.handleSessionExpired();
      throw new Error('Unauthorized');
    }

    if (!response.ok) {
      throw new Error(`Failed to fetch orders. Status: ${response.status}`);
    }

    const js = await response.json();
    const orderRequests = [];
    
    // Create an array of fetch promises for each individual order
    js?.content?.forEach(o => {
      let target = `${this.getBaseUrl()}/api/orders/${o.id}`;
      if (window.APP_CONFIG.featureFlags?.useDummyData) {
        target = `${process.env.PUBLIC_URL}/dummy-data/order-${o.id}.json`;
      }
      

      orderRequests.push(
        fetch(target, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${AuthService.getToken()}`,
          },
        })
      );
    });

    // Await all individual order fetches concurrently
    const jsons = await Promise.all(orderRequests);
    const orders = [];

    for (let i = 0; i < jsons.length; i++) {
      const element = jsons[i];
      if (element.status === 401) {
        AuthService.handleSessionExpired();
        throw new Error('Unauthorized');
      }

      if (!element.ok) {
        console.warn(`Failed to fetch details for one of the orders. Status: ${element.status}`);
        continue;
      }
      
      let o = await element.json();
      o = this.postProcessOrder(o);
      orders.push(o);
    }

    js.content = orders;
    return js;
  }

  /**
   * Transforms a raw detailed order payload into the flat structure needed for the UI.
   */
  static postProcessOrder(order) {

    order.lta = order.inputs?.lta?.requests?.length > 0;
    order.transformation = order.inputs?.processing?.transformation?.value?.length > 0;
    order.onlineData = order.inputs?.processing?.products?.map(p => p.collectionId && p.productIds?.length > 0).length > 0;

    order.status = this.deriveOrderStatus(order);

    const errorJobCount = order.jobSummary?.statusCounts?.ERROR || 0;
    order.errorMessage = order.jobSummary?.latestErrorMessage || null;
    order.errorDetails = order.jobSummary?.latestErrorDetails || null;
    order.errorJobCount = errorJobCount;

    return order;
  }

  /**
   * Derives the order's display status from `cancelledAt`, `evaluationOutcome`,
   * and the per-job statusCounts. Mirrors the priority used by mc-console-ui.
   */
  static deriveOrderStatus(order) {
    if (order.cancelledAt) return "CANCELLED";
    if (order.evaluationOutcome === "REJECTED") return "REJECTED";
    if (order.evaluationOutcome === "PENDING_APPROVAL") return "PENDING_APPROVAL";

    const counts = order.jobSummary?.statusCounts || {};
    const total = order.jobSummary?.totalJobs || 0;
    const errorJobCount = counts.ERROR || 0;

    if (errorJobCount > 0 && errorJobCount === total) return "ERROR";
    if (errorJobCount > 0)                            return "PARTIAL_ERROR";
    if ((counts.PROCESSING || 0) > 0)                 return "PROCESSING";
    if ((counts.SUSPENDED  || 0) > 0)                 return "SUSPENDED";
    if (total > 0 && (counts.COMPLETED || 0) === total) return "COMPLETED";
    if (total > 0 && (counts.REJECTED  || 0) === total) return "REJECTED";

    const allInState = ["CREATED", "QUEUED", "CANCELLED"]
      .find(s => total > 0 && counts[s] === total);
    if (allInState) return allInState;

    return "PROCESSING";
  }

  /**
   * Submits a new order payload to the API.
   * @param {Object} payload - The order configuration payload
   * @returns {Promise<Object>} The response from the server
   */
  static async createOrder(payload) {
    const endpoint = `${this.getBaseUrl()}/api/orders`;

    const response = await fetch(endpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${AuthService.getToken()}`,
      },
      body: JSON.stringify(payload),
    });

    if (response.status === 401) {
      AuthService.handleSessionExpired();
      throw new Error('Unauthorized');
    }

    if (!response.ok) {
      throw new Error(`Failed to create order. Status: ${response.status}`);
    }

    return await response.json();
  }
}

export default OrderService;